'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';


interface MeetingFormat {
  id: string;
  title: string;
  description: string;
  icon: string;
  benefits: string[];
  technicalRequirements?: string[];
  image: string;
  alt: string;
}

const MeetingFormats = () => {
  const [activeFormat, setActiveFormat] = useState<string>('office');

  const formats: MeetingFormat[] = [
  {
    id: 'office',
    title: 'Osobní schůzka v kanceláři',
    description: 'Navštivte nás v naší kanceláři v Praha-Chodov, kde vám nabízíme soukromé prostředí pro důvěrnou konzultaci.',
    icon: 'BuildingOfficeIcon',
    benefits: [
    'Osobní kontakt a budování důvěry',
    'Soukromé jednací místnosti',
    'Možnost prohlédnout si dokumenty společně',
    'Káva a občerstvení zdarma',
    'Parkování v blízkosti kanceláře'],

    image: "https://images.unsplash.com/photo-1674460614538-f043d87558dd",
    alt: 'Modern office meeting room with glass walls, wooden conference table, and comfortable chairs in Prague'
  },
  {
    id: 'online',
    title: 'Online konzultace',
    description: 'Pohodlná videokonzultace z vašeho domova nebo kanceláře. Stejně efektivní jako osobní setkání.',
    icon: 'VideoCameraIcon',
    benefits: [
    'Úspora času a cestovních nákladů',
    'Flexibilní termíny včetně večerních hodin',
    'Sdílení obrazovky pro společné prohlížení dokumentů',
    'Záznam schůzky na vyžádání',
    'Ideální pro klienty mimo Prahu'],

    technicalRequirements: [
    'Stabilní internetové připojení (min. 5 Mbps)',
    'Počítač nebo tablet s webkamerou',
    'Mikrofon a reproduktory/sluchátka',
    'Google Meet, Zoom nebo Microsoft Teams',
    'Tichý prostor bez rušení'],

    image: "https://images.unsplash.com/photo-1712904116084-1824b4853c1d",
    alt: 'Professional woman having video conference call on laptop in home office with natural lighting'
  }];


  const activeFormatData = formats.find((f) => f.id === activeFormat);

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              Vyberte si formát schůzky
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Nabízíme dva způsoby konzultace – vyberte si ten, který vám nejvíce vyhovuje
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-4 mb-8 justify-center">
            {formats.map((format) =>
            <button
              key={format.id}
              onClick={() => setActiveFormat(format.id)}
              className={`flex items-center space-x-3 px-6 py-4 rounded-lg border-2 transition-smooth ${
              activeFormat === format.id ?
              'bg-primary text-white border-primary shadow-elevated' :
              'bg-card text-text-primary border-border hover:border-primary/50 hover:shadow-card'}`
              }>

                <Icon
                name={format.icon as any}
                size={24}
                className={activeFormat === format.id ? 'text-white' : 'text-primary'} />

                <span className="font-semibold">{format.title}</span>
              </button>
            )}
          </div>

          {activeFormatData &&
          <div className="bg-card border border-border rounded-lg overflow-hidden shadow-card">
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="relative h-64 lg:h-auto overflow-hidden">
                  




                </div>

                <div className="p-6 lg:p-8">
                  <h3 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">
                    {activeFormatData.title}
                  </h3>
                  <p className="text-text-secondary mb-6">
                    {activeFormatData.description}
                  </p>

                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-text-primary mb-3 flex items-center space-x-2">
                      <Icon name="CheckBadgeIcon" size={20} className="text-success" />
                      <span>Výhody</span>
                    </h4>
                    <ul className="space-y-2">
                      {activeFormatData.benefits.map((benefit, index) =>
                    <li key={index} className="flex items-start space-x-2">
                          <Icon
                        name="CheckIcon"
                        size={16}
                        className="text-success flex-shrink-0 mt-1" />

                          <span className="text-text-secondary text-sm">{benefit}</span>
                        </li>
                    )}
                    </ul>
                  </div>

                  {activeFormatData.technicalRequirements &&
                <div className="bg-surface border border-border rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-text-primary mb-2 flex items-center space-x-2">
                        <Icon name="ComputerDesktopIcon" size={18} className="text-primary" />
                        <span>Technické požadavky</span>
                      </h4>
                      <ul className="space-y-1">
                        {activeFormatData.technicalRequirements.map((req, index) =>
                    <li key={index} className="text-text-secondary text-xs flex items-start space-x-2">
                            <span className="text-primary mt-1">•</span>
                            <span>{req}</span>
                          </li>
                    )}
                      </ul>
                    </div>
                }
                </div>
              </div>
            </div>
          }

          {activeFormat === 'office' &&
          <div className="mt-8 bg-card border border-border rounded-lg p-6 lg:p-8">
              <div className="grid lg:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-xl font-semibold text-text-primary mb-4 flex items-center space-x-2">
                    <Icon name="MapPinIcon" size={24} className="text-primary" />
                    <span>Adresa kanceláře</span>
                  </h4>
                  <p className="text-text-secondary mb-4">
                    <strong>Co-Work Financial Partners</strong><br />
                    Roztylská 2321/19<br />
                    148 00 Praha 11 - Chodov<br />
                    Česká republika
                  </p>
                  <div className="space-y-2 text-sm text-text-secondary">
                    <div className="flex items-center space-x-2">
                      <Icon name="ClockIcon" size={16} className="text-primary" />
                      <span>Po-Pá: 9:00 - 18:00</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="PhoneIcon" size={16} className="text-primary" />
                      <span>+420 123 456 789</span>
                    </div>
                  </div>
                </div>

                <div className="h-64 lg:h-full rounded-lg overflow-hidden">
                  <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title="Co-Work Financial Partners Office Location"
                  referrerPolicy="no-referrer-when-downgrade"
                  src="https://www.google.com/maps?q=50.0343,14.5186&z=15&output=embed"
                  className="border-0" />

                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </section>);

};

export default MeetingFormats;